export type PosRuntimePerformanceClass = "STANDARD" | "CONSTRAINED";

export type PosRuntimePerformanceProfile = {
  performanceClass: PosRuntimePerformanceClass;
  mediaDownloadConcurrency: number;
  backgroundSyncIntervalMs: number;
  productGridRenderBatchSize: number | null;
  secondaryReadWarmupEnabled: boolean;
};

type NavigatorWithDeviceMemory = Navigator & {
  deviceMemory?: number;
};

const STANDARD_PROFILE: PosRuntimePerformanceProfile = {
  performanceClass: "STANDARD",
  mediaDownloadConcurrency: 8,
  backgroundSyncIntervalMs: 5_000,
  productGridRenderBatchSize: null,
  secondaryReadWarmupEnabled: true,
};

const CONSTRAINED_PROFILE: PosRuntimePerformanceProfile = {
  performanceClass: "CONSTRAINED",
  mediaDownloadConcurrency: 2,
  backgroundSyncIntervalMs: 30_000,
  productGridRenderBatchSize: 16,
  secondaryReadWarmupEnabled: false,
};

let cachedProfile: PosRuntimePerformanceProfile | null = null;

function positiveFiniteNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

export function getPosRuntimePerformanceProfile(): PosRuntimePerformanceProfile {
  if (cachedProfile) return cachedProfile;
  if (typeof navigator === "undefined") return STANDARD_PROFILE;

  const runtimeNavigator = navigator as NavigatorWithDeviceMemory;
  const deviceMemoryGb = positiveFiniteNumber(runtimeNavigator.deviceMemory);
  const logicalProcessors = positiveFiniteNumber(runtimeNavigator.hardwareConcurrency);

  // Runtime capability is resolved from browser-exposed resource hints only. No device model,
  // tenant, branch, provider or Android version participates in the decision. Older runtimes
  // that cannot expose memory are treated conservatively when CPU parallelism is also limited.
  const constrained =
    (deviceMemoryGb !== null && deviceMemoryGb <= 2) ||
    (logicalProcessors !== null && logicalProcessors <= 2) ||
    (deviceMemoryGb === null && logicalProcessors !== null && logicalProcessors <= 4);

  cachedProfile = constrained ? CONSTRAINED_PROFILE : STANDARD_PROFILE;
  return cachedProfile;
}
